import { axiosInstance } from '../utils/axios'
import type { DeviceSignal, FleetSignalRow, SignalDay } from '../types/signalStrength'

/**
 * Sigfox link-quality API.
 *
 * Deliberately NOT under services/admin/: these endpoints are user-facing so
 * that the Ricster field app and API-key consumers can reach them. Admin-only
 * routes reject API keys outright, which would put signal strength out of reach
 * of the installers who most need it.
 *
 * User-facing routes use the bare `{ message, data }` envelope, unlike the
 * admin `{ success, data }` one.
 */

interface BareResponse<T> {
  message: string
  data: T
}

export const signalStrengthApi = {
  /**
   * One device's recent link quality, newest first.
   *
   * Includes every message type, so a Test message an installer triggers on
   * site shows up without waiting for the next scheduled reading.
   */
  getDeviceSignal: async (sigfoxId: string, days = 7, limit = 50): Promise<DeviceSignal> => {
    const res = await axiosInstance.get<BareResponse<DeviceSignal>>(
      `/devices/${encodeURIComponent(sigfoxId)}/signal`,
      { params: { days, limit } }
    )
    return res.data.data
  },

  /**
   * Per-day signal across the whole window, for the device chart. Asks for one
   * sample only: the samples page is the installer view's business.
   *
   * Null when the API predates the daily series, so the chart can say so
   * rather than drawing an empty window as if no messages arrived.
   */
  getDeviceSignalDaily: async (sigfoxId: string, days: number): Promise<SignalDay[] | null> => {
    const res = await axiosInstance.get<BareResponse<DeviceSignal>>(
      `/devices/${encodeURIComponent(sigfoxId)}/signal`,
      { params: { days, limit: 1, daily: true } }
    )
    return res.data.data.daily ?? null
  },

  /**
   * Fleet roll-up, one summary per device. Omitting `sigfoxIds` asks the server
   * for every device the caller may see, which is what the dashboard wants.
   * `dailyDays` adds each device's per-day series over that many days, for the
   * dashboard's strips; the summary keeps its own `days` window.
   */
  getFleetSignal: async (sigfoxIds?: string[], days = 7, dailyDays?: number): Promise<FleetSignalRow[]> => {
    const res = await axiosInstance.get<BareResponse<FleetSignalRow[]>>('/signal-status', {
      params: {
        days,
        ...(dailyDays ? { daily: true, dailyDays } : {}),
        ...(sigfoxIds && sigfoxIds.length > 0 ? { deviceIds: sigfoxIds.join(',') } : {}),
      },
    })
    return res.data.data
  },
}
